import { useEffect, useState, useRef, useCallback } from 'react';
import { useQuery } from '@apollo/client';
import { default as Heatmappper, DataPoint, Heatmap, HeatmapConfiguration } from 'heatmap.js';
import * as ss from 'simple-statistics';

import { GetClickmapDataDocument } from '../generated/graphql';
import { defaultScope as scope } from '../utility/getScope';

export const useClickmap = (
  organization: string,
  project: string,
  round: string,
  variation: string,
  segment: string | null = null,
  isGreenMode?: boolean,
  dataLimit?: number,
) => {
  const [data, setData] = useState<{ rate_x: number; rate_y: number; value: number }[] | null>();
  const [imageReady, setImageReady] = useState(false);
  const [blurRatio, setBlurRatio] = useState(1);
  const [heatmap, setHeatmap] = useState<Heatmap<'value', 'x', 'y'>>();
  const [rendered, setRendered] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const bigBlur = 0.6,
    smallBlur = 0.1;
  const bigRadius = 10,
    smallRadius = 2;
  const config: HeatmapConfiguration = {
    container: '' as any, // no need to have container but @types/heatmap.js is wrongly typed
    ...(isGreenMode
      ? {
          maxOpacity: 0.8,
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          gradient: {
            0.6: 'rgba(137, 255, 161, 1)',
            0.85: 'rgba(85, 224, 132, 1)',
            1: 'rgba(65, 203, 120, 1)',
          },
          blur: smallBlur * blurRatio <= 1 ? smallBlur * blurRatio : 1,
        }
      : {
          maxOpacity: 0.7,
          backgroundColor: 'rgba(0, 0, 100, 0.3)',
          gradient: {
            0.1: 'rgba(0, 0, 255, 1)',
            0.3: 'rgba(0, 230, 100, 1)',
            0.4: 'rgba(0, 255, 0, 0.8)',
            0.6: 'rgba(55, 255, 0, 0.7)',
            0.7: 'rgba(155, 255, 0, 0.7)',
            0.85: 'rgba(175, 255, 0, 0.6)',
            0.94: 'rgba(235, 235, 0, 0.6)',
            1: 'rgba(255, 100, 100, 0.1)',
          },
          blur: bigBlur * blurRatio <= 1 ? bigBlur * blurRatio : 1,
        }),
  };

  const {
    error,
    loading,
    data: { chart: chartData } = { chart: null },
  } = useQuery(GetClickmapDataDocument, {
    context: { clientName: 'dataplatform' },
    fetchPolicy: 'cache-first',
    variables: {
      id: `planbcd_clickmap_${organization}`,
      identifierType: 'resource_key',
      ...scope,
      breakdowns: [
        { breakdown: 'project_id', filter: [project] },
        { breakdown: 'round_id', filter: [round] },
        { breakdown: 'variation_id', filter: [variation] },
        { breakdown: 'segment', filter: segment ? [segment] : null },
      ],
      options: {
        sampling: {
          type: 'SIZE',
          number: 40000, // sample size used in Sitedock v1
        },
      },
      ...(dataLimit
        ? {
            options: {
              sampling: {
                type: 'RATE',
                number: dataLimit,
              },
            },
          }
        : {}),
    },
  });

  useEffect(() => {
    if (!chartData) return;
    const dataIndex = chartData.table.columns.indexOf('json_data');
    const rows = chartData.table.rows.flatMap((row: any) => JSON.parse(row[dataIndex].split('"').join('')));
    const xyvData = rows.flatMap((row: [number, number, number]) => {
      return { rate_x: row[0], rate_y: row[1], value: row[2] };
    });
    setData(xyvData);
  }, [chartData]);

  useEffect(() => {
    rendered && setRendered(false);
    if (!containerRef.current) {
      setImageReady(false);
      heatmap && setHeatmap(undefined);
    }
  }, [organization, project, round, variation, segment]);

  const resizeObserver = new ResizeObserver((entries: Array<ResizeObserverEntry>) => {
    const width = entries[0].contentRect.width;
    const height = entries[0].contentRect.height;
    if (height >= 100 && width >= 100) {
      setBlurRatio(width / 158);
      setImageReady(true);
    } else {
      setImageReady(false);
      heatmap && setHeatmap(undefined);
    }
    if (height >= 100 && width >= 100 && !heatmap) {
      const map = Heatmappper.create<'value', 'x', 'y'>({
        container: containerRef.current!,
        radius: bigRadius * blurRatio,
        blur: bigBlur * blurRatio <= 1 ? bigBlur * blurRatio : 1,
        backgroundColor: 'rgba(0, 0, 0, 0)',
      });
      setHeatmap(map);
      data && render({ inputHeatmap: map });
    }
  });

  const render = useCallback(
    ({
      inputData,
      inputHeatmap,
    }: {
      inputData?: Array<{ rate_x: number; rate_y: number; value: number }>;
      inputHeatmap?: Heatmap<'value', 'x', 'y'>;
    }) => {
      const heatmapObject = inputHeatmap ?? heatmap;
      const eitherofData = inputData ?? data;
      if (imageReady && heatmapObject && eitherofData) {
        heatmapObject.configure(config);

        const calculated: DataPoint<'value', 'x', 'y'>[] = eitherofData
          .map((d: { [key: string]: any }): DataPoint<'value', 'x', 'y'> | undefined => {
            if (!d.rate_x && !d.rate_y) return; // initial garbage data
            return {
              x: Math.floor(containerRef.current!.offsetWidth * d.rate_x),
              y: Math.floor(containerRef.current!.offsetHeight * d.rate_y),
              value: d.value,
              ...(isGreenMode
                ? {
                    radius: smallRadius * blurRatio,
                  }
                : {
                    radius: bigRadius * (blurRatio - 0.5),
                  }),
            };
          })
          .filter((v): v is DataPoint<'value', 'x', 'y'> => !!v);

        const mean = ss.mean(eitherofData.length ? eitherofData.map(({ value }) => value) : [0]);
        const sigma = ss.sampleStandardDeviation(
          eitherofData.length && eitherofData.length > 1 ? eitherofData.map(({ value }) => value) : [0, 0],
        );
        heatmapObject.setData({
          max: mean + sigma * 1.96,
          min: 0,
          data: calculated,
        });
        setRendered(true);
      }
    },
    [imageReady, heatmap, config, isGreenMode],
  );

  useEffect(() => {
    containerRef.current && resizeObserver.observe(containerRef.current);
    containerRef.current && data && heatmap && render({});
    return (): void => {
      //unmount
      resizeObserver.disconnect();
    };
  }, [imageReady, heatmap, data, isGreenMode]);

  return { containerRef, data, chartData, render, rendered, error, loading };
};
